import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useCategories, useSubcategories } from '../hooks/useProducts';
import { cn } from '../lib/utils';
import { 
  Search, 
  ShoppingCart, 
  User, 
  Menu, 
  X, 
  ChevronDown,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Music,
  LogOut,
  Package
} from 'lucide-react';

const Header: React.FC = () => {
  const { cart } = useCart();
  const { user, customer, signOut } = useAuth();
  const { categories } = useCategories();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [mobileExpandedItems, setMobileExpandedItems] = useState<string[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [focusedSubIndex, setFocusedSubIndex] = useState(-1);

  // Refs for keyboard navigation
  const mainNavRef = useRef<HTMLElement>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch subcategories for each category
  const { subcategories: guitarSubcategories } = useSubcategories('guitars');
  const { subcategories: keyboardSubcategories } = useSubcategories('keyboards');
  const { subcategories: drumSubcategories } = useSubcategories('drums');
  const { subcategories: studioSubcategories } = useSubcategories('studio');
  const { subcategories: accessorySubcategories } = useSubcategories('accessories');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileExpandedItems([]);
    setDropdownOpen(null);
    setUserDropdownOpen(false);
    setFocusedIndex(-1);
    setFocusedSubIndex(-1);
  }, [location]);

  // Close dropdowns on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDropdownOpen(null);
        setUserDropdownOpen(false);
        setMobileMenuOpen(false);
        setFocusedIndex(-1);
        setFocusedSubIndex(-1);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    };

    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userDropdownOpen]);

  // Build dynamic main menu items - REMOVED Home, Shop, and New Arrivals
  const mainMenuItems = React.useMemo(() => {
    // Add dynamic category items
    const categoryItems = categories.map(category => {
      let subcategories: any[] = [];
      
      switch (category.id) {
        case 'guitars':
          subcategories = guitarSubcategories.map(sub => ({ 
            name: sub.name, 
            href: `/products/${category.id}/${sub.id}` 
          }));
          break;
        case 'keyboards':
          subcategories = keyboardSubcategories.map(sub => ({ 
            name: sub.name, 
            href: `/products/${category.id}/${sub.id}` 
          }));
          break;
        case 'drums':
          subcategories = drumSubcategories.map(sub => ({ 
            name: sub.name, 
            href: `/products/${category.id}/${sub.id}` 
          }));
          break;
        case 'studio':
          subcategories = studioSubcategories.map(sub => ({ 
            name: sub.name, 
            href: `/products/${category.id}/${sub.id}` 
          }));
          break;
        case 'accessories':
          subcategories = accessorySubcategories.map(sub => ({ 
            name: sub.name, 
            href: `/products/${category.id}/${sub.id}` 
          }));
          break;
      }

      return {
        name: category.name,
        href: `/products/${category.id}`,
        subcategories
      };
    });

    // Add Services menu item
    const servicesItem = {
      name: 'Deals & Rentals',
      href: '/services',
      subcategories: [
        { name: 'Rental', href: '/rental' },
        { name: 'Open Box', href: '/open-box' },
        { name: 'Factory Returned', href: '/factory-returned' }
      ]
    };

    const endItems = [
      servicesItem,
      { 
        name: 'About', 
        href: '/about',
        subcategories: []
      },
      { 
        name: 'Contact', 
        href: '/contact',
        subcategories: []
      }
    ];

    return [...categoryItems, ...endItems];
  }, [categories, guitarSubcategories, keyboardSubcategories, drumSubcategories, studioSubcategories, accessorySubcategories]);

  // Function to check if a menu item is active
  const isActiveMenuItem = (item: typeof mainMenuItems[0]) => {
    const currentPath = location.pathname;
    
    // Check if current path matches the main item href
    if (currentPath === item.href) return true;
    
    // Check if current path matches any subcategory href
    return item.subcategories.some(sub => currentPath === sub.href || currentPath.startsWith(sub.href + '/'));
  };

  const handleMouseEnter = (itemName: string) => {
    setDropdownOpen(itemName);
  };

  const handleMouseLeave = () => {
    setDropdownOpen(null);
    setFocusedSubIndex(-1);
  };

  const toggleMobileExpanded = (itemName: string) => {
    setMobileExpandedItems(prev => 
      prev.includes(itemName) 
        ? prev.filter(item => item !== itemName)
        : [...prev, itemName]
    );
  };

  const handleSignOut = async () => {
    await signOut();
    setUserDropdownOpen(false);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/products?search=${encodeURIComponent(searchQuery.trim())}`;
    }
  };

  // Keyboard navigation handlers
  const handleMainNavKeyDown = (e: React.KeyboardEvent, index: number, itemName: string) => {
    const item = mainMenuItems[index];
    
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        const nextIndex = (index + 1) % mainMenuItems.length;
        setFocusedIndex(nextIndex);
        const nextButton = mainNavRef.current?.children[nextIndex]?.querySelector('button, a') as HTMLElement;
        nextButton?.focus();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        const prevIndex = index === 0 ? mainMenuItems.length - 1 : index - 1;
        setFocusedIndex(prevIndex);
        const prevButton = mainNavRef.current?.children[prevIndex]?.querySelector('button, a') as HTMLElement;
        prevButton?.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (item.subcategories.length > 0) {
          setDropdownOpen(itemName);
          setFocusedSubIndex(0);
          setTimeout(() => {
            const firstSubItem = dropdownRefs.current[itemName]?.querySelector('a') as HTMLElement;
            firstSubItem?.focus();
          }, 0);
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (item.subcategories.length > 0) {
          setDropdownOpen(dropdownOpen === itemName ? null : itemName);
        }
        break;
    }
  };

  const handleDropdownKeyDown = (e: React.KeyboardEvent, itemName: string, subItems: any[]) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextSubIndex = (focusedSubIndex + 1) % (subItems.length + 1); // +1 for "View All" link
        setFocusedSubIndex(nextSubIndex);
        const nextSubItem = dropdownRefs.current[itemName]?.querySelectorAll('a')[nextSubIndex] as HTMLElement;
        nextSubItem?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevSubIndex = focusedSubIndex === 0 ? subItems.length : focusedSubIndex - 1;
        setFocusedSubIndex(prevSubIndex);
        const prevSubItem = dropdownRefs.current[itemName]?.querySelectorAll('a')[prevSubIndex] as HTMLElement;
        prevSubItem?.focus();
        break;
      case 'Escape':
        e.preventDefault();
        setDropdownOpen(null);
        setFocusedSubIndex(-1);
        const mainButton = mainNavRef.current?.children[focusedIndex]?.querySelector('button, a') as HTMLElement;
        mainButton?.focus();
        break;
    }
  };

  return (
    <header className="w-full" role="banner">
      {/* Main Header */}
      <div className={cn(
        "bg-white shadow-md transition-all duration-300 sticky top-0 z-50",
        isScrolled ? "shadow-lg" : "shadow-sm"
      )}>
        <div className="container-custom">
          {/* Desktop Header Layout */}
          <div className={cn(
            "hidden lg:grid lg:grid-cols-[1fr_auto_1fr] items-center gap-8 transition-all duration-300",
            isScrolled ? "h-16 py-3" : "h-24 py-5"
          )}>
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center justify-self-center focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-offset-2 rounded-md"
              aria-label="Strum Music - Go to homepage"
            >
              <img 
                src="/StrumMusic_Logo_Final.png" 
                alt="Strum Music" 
                className={cn(
                  "transition-all duration-300 object-contain justify-self-center",
                  isScrolled ? "h-12 max-h-12" : "h-16 max-h-16"
                )} 
              />
            </Link>

            {/* Search Bar */}
            <form onSubmit={handleSearchSubmit} className="relative max-w-2xl w-full">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input 
                type="text"
                placeholder="Search for instruments, accessories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-full border border-gray-300 bg-gray-50 py-3 pl-12 pr-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-red-700 focus:bg-white transition-colors"
                aria-label="Search for instruments and accessories"
              />
            </form>

            {/* Right Side Actions */}
            <div className="flex items-center justify-end space-x-6" role="toolbar" aria-label="Utility navigation">
              {/* Contact Info */}
              <a 
                href="tel:+918076883098"
                className="flex items-center space-x-2 text-gray-700 hover:text-red-700 transition-colors group"
              >
                <Phone className="h-5 w-5 group-hover:text-red-700" />
                <span className="text-sm font-medium">+91-8076883098</span>
              </a>

              {/* User Account Dropdown */}
              <div className="relative" ref={userDropdownRef}>
                <button
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  className="flex items-center space-x-1 p-2 text-gray-700 hover:text-red-700 focus:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-offset-1 rounded-md transition-colors"
                  aria-label="User account"
                >
                  <User className="h-5 w-5" />
                  <span className="text-sm font-medium">My Account</span>
                </button>

                {/* User Dropdown Menu */}
                {userDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-2">
                    {user ? (
                      <>
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900">
                            {customer?.full_name || 'Welcome!'}
                          </p>
                          <p className="text-sm text-gray-600">{user.email}</p>
                        </div>
                        <div className="py-1">
                          <Link
                            to="/profile"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            onClick={() => setUserDropdownOpen(false)}
                          >
                            <User className="h-4 w-4 mr-3" />
                            My Profile
                          </Link>
                          <Link
                            to="/my-orders"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            onClick={() => setUserDropdownOpen(false)}
                          >
                            <Package className="h-4 w-4 mr-3" />
                            My Orders
                          </Link>
                          <button
                            onClick={handleSignOut}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            <LogOut className="h-4 w-4 mr-3" />
                            Sign Out
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="py-1">
                        <Link
                          to="/login"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          onClick={() => setUserDropdownOpen(false)}
                        >
                          <User className="h-4 w-4 mr-3" />
                          Sign In
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Cart Icon */}
              <Link 
                to="/cart" 
                className="flex items-center space-x-1 p-2 text-gray-700 hover:text-red-700 focus:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-offset-1 rounded-md transition-colors relative"
                aria-label={`Shopping cart with ${cart.itemCount} items`}
              >
                <ShoppingCart className="h-5 w-5" />
                <span className="text-sm font-medium">Cart</span>
                {cart.itemCount > 0 && (
                  <span 
                    className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-700 text-xs text-white font-medium"
                    aria-label={`${cart.itemCount} items in cart`}
                  >
                    {cart.itemCount}
                  </span>
                )}
              </Link>
            </div>
          </div>

          {/* Mobile Header Layout */}
          <div className={cn(
            "flex lg:hidden items-center justify-between transition-all duration-300",
            isScrolled ? "h-16 py-2" : "h-20 py-4"
          )}>
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-offset-2 rounded-md"
              aria-label="Strum Music - Go to homepage"
            >
              <img 
                src="/StrumMusic_Logo_Final.png" 
                alt="Strum Music" 
                className={cn(
                  "transition-all duration-300 object-contain",
                  isScrolled ? "h-8 max-h-8" : "h-12 max-h-12"
                )} 
              />
            </Link>

            {/* Mobile Right Actions */}
            <div className="flex items-center space-x-4">
              {/* Cart Icon */}
              <Link 
                to="/cart" 
                className="p-2 text-gray-700 hover:text-red-700 focus:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-offset-1 rounded-md transition-colors relative"
                aria-label={`Shopping cart with ${cart.itemCount} items`}
              >
                <ShoppingCart className="h-5 w-5" />
                {cart.itemCount > 0 && (
                  <span 
                    className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-700 text-xs text-white font-medium"
                    aria-label={`${cart.itemCount} items in cart`}
                  >
                    {cart.itemCount}
                  </span>
                )}
              </Link>

              {/* Mobile Menu toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="rounded-md p-2 text-red-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-offset-1 transition-colors"
                aria-label={mobileMenuOpen ? "Close mobile menu" : "Open mobile menu"}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Desktop Navigation Bar */}
        <div className="hidden lg:block border-t border-red-100 bg-red-700">
          <div className="container-custom">
            <nav 
              className="flex items-center justify-center space-x-8 py-4" 
              role="navigation" 
              aria-label="Main navigation"
              ref={mainNavRef}
            >
              {mainMenuItems.map((item, index) => {
                const isActive = isActiveMenuItem(item);
                const hasSubcategories = item.subcategories.length > 0;
                
                return (
                  <div
                    key={item.name}
                    className="relative"
                    onMouseEnter={() => hasSubcategories && handleMouseEnter(item.name)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {hasSubcategories ? (
                      <button
                        onClick={() => setDropdownOpen(dropdownOpen === item.name ? null : item.name)}
                        onKeyDown={(e) => handleMainNavKeyDown(e, index, item.name)}
                        className={cn(
                          "flex items-center space-x-1 px-4 py-2 text-sm font-medium transition-all duration-200 rounded-md relative group",
                          isActive 
                            ? "text-white bg-red-800" 
                            : "text-red-100 hover:text-white hover:bg-red-800 focus:text-white focus:bg-red-800",
                          "focus:outline-none focus:ring-2 focus:ring-red-300"
                        )}
                        aria-expanded={dropdownOpen === item.name}
                        aria-haspopup="true"
                        aria-controls={`dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        <span>{item.name}</span>
                        <ChevronDown className="h-4 w-4 opacity-70" aria-hidden="true" />
                      </button>
                    ) : (
                      <Link
                        to={item.href}
                        className={cn(
                          "flex items-center px-4 py-2 text-sm font-medium transition-all duration-200 rounded-md",
                          isActive 
                            ? "text-white bg-red-800" 
                            : "text-red-100 hover:text-white hover:bg-red-800 focus:text-white focus:bg-red-800",
                          "focus:outline-none focus:ring-2 focus:ring-red-300"
                        )}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        {item.name}
                      </Link>
                    )}

                    {/* Dropdown Menu - Fixed positioning to eliminate gap */}
                    {hasSubcategories && dropdownOpen === item.name && (
                      <div 
                        ref={(el) => dropdownRefs.current[item.name] = el}
                        id={`dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className="absolute top-full left-0 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-2"
                        style={{ marginTop: '0px' }} // Remove any margin that could create a gap
                        role="menu"
                        aria-labelledby={`button-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        onKeyDown={(e) => handleDropdownKeyDown(e, item.name, item.subcategories)}
                        onMouseEnter={() => setDropdownOpen(item.name)} // Keep dropdown open when hovering over it
                        onMouseLeave={handleMouseLeave}
                      >
                        <div className="px-3 py-2 border-b border-gray-100">
                          <h3 className="text-sm font-semibold text-gray-900">{item.name}</h3>
                        </div>
                        <div className="p-2 space-y-1">
                          {item.subcategories.map((subcategory) => {
                            const isSubActive = location.pathname === subcategory.href || location.pathname.startsWith(subcategory.href + '/');
                            
                            return (
                              <Link
                                key={subcategory.name}
                                to={subcategory.href}
                                className={cn(
                                  "block px-3 py-2 text-sm rounded-md transition-colors duration-150",
                                  isSubActive 
                                    ? "text-red-700 bg-red-50 font-medium" 
                                    : "text-gray-700 hover:text-red-700 hover:bg-red-50 focus:text-red-700 focus:bg-red-50",
                                  "focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-700"
                                )}
                                role="menuitem"
                                tabIndex={-1}
                                aria-current={isSubActive ? 'page' : undefined}
                              >
                                {subcategory.name}
                              </Link>
                            );
                          })}
                        </div>
                        {/* View All Link */}
                        <div className="border-t border-gray-100 px-3 py-2">
                          <Link
                            to={item.href}
                            className="block text-sm font-medium text-red-700 hover:text-red-800 focus:text-red-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-700 rounded-md px-2 py-1 transition-colors"
                            role="menuitem"
                            tabIndex={-1}
                          >
                            View All {item.name} →
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>
      </div>
        
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div 
          id="mobile-menu"
          className="lg:hidden border-t border-gray-100 bg-white max-h-screen overflow-y-auto sticky top-16 z-40 shadow-lg"
          role="navigation"
          aria-label="Mobile navigation"
          ref={mobileMenuRef}
        >
          <div className="container-custom py-4">
            <nav className="flex flex-col space-y-1">
              {/* Search Section */}
              <div className="mb-4">
                <form onSubmit={handleSearchSubmit} className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                  <input 
                    type="text"
                    placeholder="Search for instruments, accessories..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-gray-50 py-3 pl-10 pr-4 text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-red-700 focus:bg-white transition-colors"
                    aria-label="Search for instruments and accessories"
                  />
                </form>
              </div>

              {/* Utility Links Section */}
              <div className="mb-4 pb-4 border-b border-gray-200">
                <div className="grid grid-cols-2 gap-2" role="toolbar" aria-label="Utility links">
                  <a 
                    href="tel:+918076883098" 
                    className="flex flex-col items-center py-3 px-2 text-gray-700 hover:text-red-700 hover:bg-red-50 focus:text-red-700 focus:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-offset-1 rounded-md transition-colors"
                    aria-label="Call us"
                  >
                    <Phone className="h-5 w-5 mb-1" />
                    <span className="text-xs font-medium">Call</span>
                  </a>
                  
                  {user ? (
                    <Link 
                      to="/profile"
                      className="flex flex-col items-center py-3 px-2 text-gray-700 hover:text-red-700 hover:bg-red-50 focus:text-red-700 focus:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-offset-1 rounded-md transition-colors"
                      aria-label="My profile"
                    >
                      <User className="h-5 w-5 mb-1" />
                      <span className="text-xs font-medium">Profile</span>
                    </Link>
                  ) : (
                    <Link 
                      to="/login"
                      className="flex flex-col items-center py-3 px-2 text-gray-700 hover:text-red-700 hover:bg-red-50 focus:text-red-700 focus:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-offset-1 rounded-md transition-colors"
                      aria-label="Sign in"
                    >
                      <User className="h-5 w-5 mb-1" />
                      <span className="text-xs font-medium">Sign In</span>
                    </Link>
                  )}
                </div>
              </div>
              
              {/* Main Menu Items */}
              <div className="space-y-1" role="menu">
                {mainMenuItems.map((item) => {
                  const isActive = isActiveMenuItem(item);
                  const hasSubcategories = item.subcategories.length > 0;
                  
                  return (
                    <div key={item.name} role="none">
                      <div className="flex items-center">
                        <Link 
                          to={item.href}
                          className={cn(
                            "flex-1 px-4 py-3 rounded-md transition-colors font-medium text-base",
                            isActive 
                              ? "text-red-700 bg-red-50" 
                              : "text-gray-700 hover:text-red-700 hover:bg-red-50 focus:text-red-700 focus:bg-red-50",
                            "focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-700"
                          )}
                          role="menuitem"
                          aria-current={isActive ? 'page' : undefined}
                        >
                          {item.name}
                        </Link>
                        {hasSubcategories && (
                          <button
                            onClick={() => toggleMobileExpanded(item.name)}
                            className="p-2 text-gray-500 hover:text-red-700 focus:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-offset-1 rounded-md transition-colors"
                            aria-label={`${mobileExpandedItems.includes(item.name) ? 'Collapse' : 'Expand'} ${item.name} submenu`}
                            aria-expanded={mobileExpandedItems.includes(item.name)}
                            aria-controls={`mobile-submenu-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <ChevronRight 
                              className={cn(
                                "h-4 w-4 transition-transform duration-200",
                                mobileExpandedItems.includes(item.name) ? "rotate-90" : ""
                              )}
                            />
                          </button>
                        )}
                      </div>
                      
                      {/* Expandable Subcategories */}
                      {hasSubcategories && mobileExpandedItems.includes(item.name) && (
                        <div 
                          id={`mobile-submenu-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className="ml-4 mt-1 space-y-1 border-l-2 border-red-100 pl-4"
                          role="menu"
                          aria-labelledby={`button-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {item.subcategories.map((subcategory) => {
                            const isSubActive = location.pathname === subcategory.href || location.pathname.startsWith(subcategory.href + '/');
                            
                            return (
                              <Link
                                key={subcategory.name}
                                to={subcategory.href}
                                className={cn(
                                  "block px-3 py-2 text-sm rounded-md transition-colors",
                                  isSubActive 
                                    ? "text-red-700 bg-red-50 font-medium" 
                                    : "text-gray-600 hover:text-red-700 hover:bg-red-50 focus:text-red-700 focus:bg-red-50",
                                  "focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-700"
                                )}
                                role="menuitem"
                                aria-current={isSubActive ? 'page' : undefined}
                              >
                                {subcategory.name}
                              </Link>
                            );
                          })}
                          <Link
                            to={item.href}
                            className="block px-3 py-2 text-sm font-medium text-red-700 hover:text-red-800 hover:bg-red-50 focus:text-red-800 focus:bg-red-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-700 rounded-md transition-colors"
                            role="menuitem"
                          >
                            View All {item.name} →
                          </Link>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* User Account Section for Mobile */}
              {user && (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <div className="px-4 py-2 text-sm text-gray-600">
                    Signed in as {customer?.full_name || user.email}
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 rounded-md transition-colors"
                  >
                    <LogOut className="h-4 w-4 mr-3" />
                    Sign Out
                  </button>
                </div>
              )}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;